<script lang="ts">
  /**
   * Mail administration (Settings → Mail administration), shown only to the mailbox listed
   * as an admin of this site in tenants.json. The server decides that; this component only
   * renders what `GET /api/admin/whoami` reports, and every route it calls is enforced again
   * on the server and once more inside the privileged helper.
   *
   * Passwords set here change the real mail account, so the reset form says so out loud.
   */
  import { ApiError, api } from '$lib/api/client';
  import { t } from '$lib/i18n';
  import { formatQuota } from '$lib/mail/format';
  import type { AdminMailbox } from '$lib/api/types';

  const USER_RE = /^[a-z0-9]([a-z0-9._-]{0,30}[a-z0-9])?$/;

  let available = $state(false);
  let domain = $state('');
  let boxes = $state<AdminMailbox[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let note = $state<string | null>(null);

  // create form
  let adding = $state(false);
  let newUser = $state('');
  let newReal = $state('');
  let newPass = $state('');
  let newQuota = $state('1024');

  // per-row forms
  let resetting = $state<string | null>(null);
  let resetPass = $state('');
  let quotaFor = $state<string | null>(null);
  let quotaMb = $state('');

  let busy = $state(false);
  // The generated password is the only copy anyone will ever see: the server stores a hash, so
  // clearing the field on success would leave the admin with nothing to hand to the new user.
  let issued = $state<{ email: string; password: string } | null>(null);
  let copied = $state(false);

  $effect(() => {
    void boot();
  });

  async function boot(): Promise<void> {
    try {
      const who = await api.adminWhoami();
      available = who.admin && who.available;
      domain = who.domains[0] ?? '';
      if (available) await refresh();
    } catch {
      available = false;
    }
  }

  async function refresh(): Promise<void> {
    loading = true;
    error = null;
    try {
      const r = await api.adminMailboxes(domain || undefined);
      boxes = r.mailboxes;
      domain = r.domain;
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'admin_unavailable' ? 'adminUnavailable' : 'adminFailed');
    } finally {
      loading = false;
    }
  }

  function failure(e: unknown): string {
    if (e instanceof ApiError) {
      if (e.code === 'already_exists') return t('adminExists');
      if (e.code === 'bad_user') return t('adminBadUser');
      if (e.code === 'password_too_short' || e.code === 'password_contains_user') return t('adminPasswordShort');
      if (e.code === 'admin_unavailable') return t('adminUnavailable');
      if (e.code === 'quota_unavailable') return t('adminQuotaUnavailable');
    }
    return t('adminFailed');
  }

  /** A readable, high-entropy suggestion so an admin never invents a weak one. */
  function generate(): string {
    const alphabet = 'abcdefghijkmnpqrstuvwxyz23456789';
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
      out += alphabet[bytes[i]! % alphabet.length];
      if (i % 4 === 3 && i < bytes.length - 1) out += '-';
    }
    return out;
  }

  async function create(): Promise<void> {
    const user = newUser.trim().toLowerCase();
    if (!USER_RE.test(user)) { error = t('adminBadUser'); return; }
    if (newPass.length < 10) { error = t('adminPasswordShort'); return; }
    busy = true;
    error = null;
    note = null;
    try {
      const r = await api.adminCreateMailbox({
        user, password: newPass, realName: newReal.trim() || undefined,
        quotaMb: Number(newQuota) || 0, domain,
      });
      const email = r.mailbox?.email ?? `${user}@${domain}`;
      // The mailbox can be created while the quota step fails (disk quotas off on the server);
      // saying so is more useful than a success line that quietly means something else.
      note = r.quotaApplied === false
        ? t('adminCreatedNoQuota', { email })
        : t('adminCreated', { email });
      issued = { email, password: newPass };
      adding = false;
      newUser = '';
      newReal = '';
      newPass = '';
      await refresh();
    } catch (e) {
      error = failure(e);
    } finally {
      busy = false;
    }
  }

  async function resetPassword(box: AdminMailbox): Promise<void> {
    if (resetPass.length < 10) { error = t('adminPasswordShort'); return; }
    busy = true;
    error = null;
    note = null;
    try {
      await api.adminSetPassword({ user: box.user, password: resetPass, domain });
      note = t('adminResetDone', { email: box.email });
      issued = { email: box.email, password: resetPass };
      resetting = null;
      resetPass = '';
    } catch (e) {
      error = failure(e);
    } finally {
      busy = false;
    }
  }

  async function saveQuota(box: AdminMailbox): Promise<void> {
    busy = true;
    error = null;
    note = null;
    try {
      await api.adminSetQuota({ user: box.user, quotaMb: Number(quotaMb) || 0, domain });
      note = t('adminQuotaDone', { email: box.email });
      quotaFor = null;
      await refresh();
    } catch (e) {
      error = failure(e);
    } finally {
      busy = false;
    }
  }

  function openQuota(box: AdminMailbox): void {
    quotaFor = box.user;
    quotaMb = box.quotaBytes ? String(Math.round(box.quotaBytes / 1024 / 1024)) : '0';
    resetting = null;
  }

  function openReset(box: AdminMailbox): void {
    resetting = box.user;
    resetPass = generate();
    quotaFor = null;
    issued = null;
  }

  async function copyIssued(): Promise<void> {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(`${issued.email}\n${issued.password}`);
      copied = true;
      setTimeout(() => { copied = false; }, 2000);
    } catch {
      /* clipboard blocked: the value is on screen to copy by hand */
    }
  }
</script>

{#if available}
  <section class="admin">
    <h3>{t('adminSection')}</h3>
    <div class="card">
      <p class="tik-hint">{t('adminIntro', { domain })}</p>
      {#if note}<p class="tik-note" role="status">{note}</p>{/if}
      {#if error}<p class="tik-error" role="alert">{error}</p>{/if}

      {#if issued}
        <div class="issued">
          <p class="tik-hint">{t('adminIssuedIntro')}</p>
          <div class="pair" dir="ltr">
            <span>{issued.email}</span>
            <code>{issued.password}</code>
          </div>
          <div class="tik-actions">
            <button type="button" class="tik-btn" onclick={copyIssued}>{copied ? t('twoFactorCopied') : t('twoFactorCopy')}</button>
            <button type="button" class="tik-btn primary" onclick={() => { issued = null; }}>{t('twoFactorDone')}</button>
          </div>
        </div>
      {/if}

      <ul class="boxes">
        {#each boxes as box (box.user)}
          <li>
            <div class="head">
              <span class="addr" dir="ltr">{box.email}</span>
              <span class="use">
                {formatQuota(box.usedBytes)}
                {#if box.quotaBytes}/ {formatQuota(box.quotaBytes)}{:else}/ {t('adminNoLimit')}{/if}
              </span>
            </div>
            <div class="tik-actions">
              <button type="button" class="tik-btn" onclick={() => openReset(box)}>{t('adminReset')}</button>
              <button type="button" class="tik-btn" onclick={() => openQuota(box)}>{t('adminQuotaSet')}</button>
            </div>

            {#if resetting === box.user}
              <div class="form tik-fields">
                <p class="tik-warn">{t('adminResetWarn')}</p>
                <label>
                  <span>{t('adminResetFor', { email: box.email })}</span>
                  <input class="tik-input mono" type="text" dir="ltr" spellcheck="false" bind:value={resetPass} />
                </label>
                <div class="tik-actions">
                  <button type="button" class="tik-btn" onclick={() => { resetPass = generate(); }}>{t('adminGenerate')}</button>
                  <button type="button" class="tik-btn" onclick={() => { resetting = null; }}>{t('adminCancel')}</button>
                  <button type="button" class="tik-btn primary" disabled={busy} onclick={() => resetPassword(box)}>{t('adminSave')}</button>
                </div>
              </div>
            {/if}

            {#if quotaFor === box.user}
              <div class="form tik-fields">
                <label>
                  <span>{t('adminQuotaMb')}</span>
                  <input class="tik-input" type="number" dir="ltr" min="0" step="64" bind:value={quotaMb} />
                </label>
                <div class="tik-actions">
                  <button type="button" class="tik-btn" onclick={() => { quotaFor = null; }}>{t('adminCancel')}</button>
                  <button type="button" class="tik-btn primary" disabled={busy} onclick={() => saveQuota(box)}>{t('adminSave')}</button>
                </div>
              </div>
            {/if}
          </li>
        {/each}
        {#if !boxes.length && !loading}
          <li class="muted empty">{t('adminMailboxes')}: 0</li>
        {/if}
      </ul>

      {#if adding}
        <div class="form tik-fields">
          <label>
            <span>{t('adminUser')}</span>
            <input class="tik-input" type="text" dir="ltr" spellcheck="false" bind:value={newUser} placeholder="sales" />
          </label>
          <label>
            <span>{t('adminRealName')}</span>
            <input class="tik-input" type="text" bind:value={newReal} />
          </label>
          <label>
            <span>{t('adminPassword')}</span>
            <input class="tik-input mono" type="text" dir="ltr" spellcheck="false" bind:value={newPass} />
          </label>
          <label>
            <span>{t('adminQuotaMb')}</span>
            <input class="tik-input" type="number" dir="ltr" min="0" step="64" bind:value={newQuota} />
          </label>
          <div class="tik-actions">
            <button type="button" class="tik-btn" onclick={() => { newPass = generate(); }}>{t('adminGenerate')}</button>
            <button type="button" class="tik-btn" onclick={() => { adding = false; }}>{t('adminCancel')}</button>
            <button type="button" class="tik-btn primary" disabled={busy} onclick={create}>{t('adminCreate')}</button>
          </div>
        </div>
      {:else}
        <div class="tik-actions">
          <button type="button" class="tik-btn primary" onclick={() => { adding = true; newPass = generate(); error = null; }}>{t('adminAdd')}</button>
        </div>
      {/if}
    </div>
  </section>
{/if}

<style>

  /* @css settings/AdminMailboxes start */
  /* Controls come from the shared .tik-* classes in styles/global.css; only the panel's own
     layout lives here. */
  .card {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .boxes {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .boxes > li {
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    padding: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .addr {
    font-weight: 600;
    color: var(--text-strong);
  }
  .use {
    font-family: var(--font-mono);
    font-size: var(--text-xs, 12px);
    color: var(--text-muted);
    direction: ltr;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    border-top: 1px dashed var(--border-default);
    padding-top: var(--space-3);
  }
  .form label {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-strong);
  }
  /* The one copy of a new password anyone will see. */
  .issued {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    background: var(--surface-subtle);
  }
  .pair {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    align-items: baseline;
  }
  .pair code {
    font-family: var(--font-mono);
    background: var(--surface-card);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    padding: 4px var(--space-2);
    user-select: all;
  }
  .empty {
    border: 0;
    padding: 0;
  }
  /* @css settings/AdminMailboxes end */
</style>
